import React from 'react';
import { StyleSheet, css } from 'aphrodite/no-important';

import { titleCase, FLAG_VALUE, calculateAge, debugLogToConsole } from '../../utils';
import CaseCriticalIcon from '../svg-icons/case-critical-icon';
import CaseWarningIcon from '../svg-icons/case-warning-icon';
import CaseSuccessIcon from '../svg-icons/case-success-icon';

const PatientCard = (props) => {
  const styles = StyleSheet.create({
    patientCard: {
      position: 'relative',
      width: '318px',
      minWidth: '318px',
      height: '120px',
      background: 'white',
      marginLeft: '0',
      borderRadius: '4px'
    },
    patientCardField: {
      color: '#5080B0',
      fontWeight: '700',
      fontSize: '12px'
    },
    patientCardFieldValue: {
      color: 'black',
      fontSize: '12px',
      flexGrow: '1'
      // maxWidth: '200px'
    }
  });

  const {
    dos = '',
    dob = '',
    gender = '',
    patientType = '',
    surgeons = [],
    anesthesia = [],
    admDx = [],
    otherAdmDx = [],
    surgicalProcedures = [],
    otherSurgicalProcedures = [],
    firstName = '',
    lastName = ''
  } = props.patientData;

  let tmpDos = null;
  let dosY = '';
  let dosM = '';
  let dosD = '';
  if (dos.length > 0) {
    tmpDos = dos.split('T');
    const tmp = tmpDos[0].split('-');
    dosY = tmp[0];
    dosM = tmp[1];
    dosD = tmp[2];
  }

  let tmpDob = null;
  let dobY = '';
  let dobM = '';
  let dobD = '';
  if (dob.length > 0) {
    tmpDob = dob.split('T');
    const tmp = tmpDob[0].split('-');
    dobY = tmp[0];
    dobM = tmp[1];
    dobD = tmp[2];
  }

  let ptType = '';
  if (patientType.length > 0 && patientType !== 'Not Set') {
    ptType = patientType.startsWith('In') ? 'In Pt' : 'Out Pt';
  } else {
    ptType = 'Not Set';
  }

  let patientString = '';
  if (lastName.length > 0 && firstName.length > 0) {
    const pName = `${titleCase(lastName)}, ${titleCase(firstName)}`;
    const ptGender = gender.toLowerCase() === 'male' ? 'M' : 'F';
    const pDob = `${dobM}/${dobD}/${dobY}`;
    const pAge = calculateAge(pDob);
    patientString = `${pName} (${pAge}${ptGender}, ${pDob})`;
  }

  let surgeonsStr = '';
  surgeons.forEach((surgeon) => {
    surgeonsStr += (titleCase(surgeon.fullName) || titleCase(surgeon.displayName) || `${titleCase(surgeon.providerLastName)}, ${titleCase(surgeon.providerFirstName)}`) + ' | ';
  });

  let anesthesiaStr = '';
  anesthesia.forEach((anes) => {
    anesthesiaStr += (titleCase(anes.fullName) || titleCase(anes.displayName) || `${titleCase(anes.providerLastName)}, ${titleCase(anes.providerFirstName)}`) + ' | ';
  });

  let admDxStr = '';
  (admDx.concat(otherAdmDx)).forEach((dx, idx) => {
    admDxStr += dx.description + ' | ';
  });

  let procsStr = '';
  (surgicalProcedures.concat(otherSurgicalProcedures)).forEach((dx) => {
    procsStr += dx.description + ' | ';
  });

  return (
    <div className={`p-1 ${css(styles.patientCard)}`}>
      {/* Patient */}
      <div className='fx'>
        <div
          className={`ml-1 fx fx__v-center ${css(styles.patientCardField)}`}
          style={{ width: '45px' }}
        >
          Patient
        </div>
        {patientString.length > 0 &&
          <div className={`ml-1 text-truncate ${css(styles.patientCardFieldValue)}`}>
            {patientString}
          </div>
        }
      </div>
      {/* MR# and Account# */}
      <div className='fx'>
        {/* MR# */}
        <div className={`ml-1 fx fx__v-center ${css(styles.patientCardField)}`}>MR#</div>
        {/* MR# - field value */}
        <div
          className={`ml-1 ${css(styles.patientCardFieldValue)}`}
          style={{ flexGrow: '0', minWidth: '100px' }}
        />
        {/* Account # */}
        <div className={`ml-2 fx fx__v-center ${css(styles.patientCardField)}`}>Account#</div>
        <div className={`ml-2 ${css(styles.patientCardFieldValue)}`} />
      </div>
      {/* DOS & Patient Type */}
      <div className='fx'>
        {/* Date of Surgery - DOS (Field) */}
        <div className={`ml-1 fx fx__v-center ${css(styles.patientCardField)}`}>DOS</div>
        {/* DOS - field value */}
        <div
          className={`ml-1 ${css(styles.patientCardFieldValue)}`}
          style={{ flexGrow: '0', minWidth: '100px' }}
        >
          {dosM.length > 0 && dosD.length > 0 && dosY.length > 0
            ? `${dosM}/${dosD}/${dosY}`
            : 'TBD'
          }
        </div>
        {/* Patient Type */}
        <div className={`ml-2 fx fx__v-center ${css(styles.patientCardField)}`}>Adm Type</div>
        <div className={`ml-2 ${css(styles.patientCardFieldValue)}`}>
          {ptType}
        </div>
      </div>
      {/* Surgeon */}
      <div className='fx'>
        <div
          className={`ml-1 text-truncate fx fx__v-center ${css(styles.patientCardField)}`}
          style={{ minWidth: '45px' }}
        >
          Surgeon
        </div>
        <div className={`ml-1 text-truncate ${css(styles.patientCardFieldValue)}`}>
          {surgeonsStr.substring(0, surgeonsStr.length - 2)}
        </div>
      </div>
      {/* Surgery/Procedures */}
      <div className='fx'>
        <div
          className={`ml-1 text-truncate fx fx__v-center ${css(styles.patientCardField)}`}
          style={{ minWidth: '45px' }}
        >
          Procedures
        </div>
        <div className={`ml-1 text-truncate ${css(styles.patientCardFieldValue)}`}>
          {procsStr.substring(0, procsStr.length - 2)}
        </div>
      </div>
      {/* Anesthesia */}
      <div className='fx'>
        <div
          className={`ml-1 text-truncate fx fx__v-center ${css(styles.patientCardField)}`}
          style={{ minWidth: '45px' }}
        >
          Anesthesia
        </div>
        <div className={`ml-1 text-truncate ${css(styles.patientCardFieldValue)}`}>
          {anesthesiaStr.substring(0, anesthesiaStr.length - 2)}
        </div>
      </div>
      {/* Case Posting Icon */}
      <div
        onClick={props.onClickCase}
        style={{
          position: 'absolute',
          top: '4px',
          right: '3px'
        }}
      >
        {props.flagType === FLAG_VALUE.critical &&
          <CaseCriticalIcon />
        }
        {props.flagType === FLAG_VALUE.warning &&
          <CaseWarningIcon />
        }
        {props.flagType === FLAG_VALUE.success &&
          <CaseSuccessIcon />
        }
      </div>
    </div>
  );
};

export default PatientCard;
